import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './Button';
import { Modal } from './Modal';
import { getTransparentImageStyles } from '../../utils/imageUtils';

interface ImageEditorProps {
  currentImage?: string;
  onImageChange: (imageData: string | null) => void;
  disabled?: boolean;
  className?: string;
}
 
const ALPHA_SAFE_TYPES = new Set(['image/png', 'image/webp', 'image/avif', 'image/gif']);
 
function getMimeFromDataUrl(dataUrl?: string): string | undefined {
  if (!dataUrl) return undefined;
  const match = dataUrl.match(/^data:([^;]+);base64,/i);
  return match?.[1];
}
 
function pickOutputType(preferred?: string, fallback: string = 'image/png') {
  if (preferred && ALPHA_SAFE_TYPES.has(preferred)) return preferred;
  return fallback;
}

export const ImageEditor: React.FC<ImageEditorProps> = ({
  currentImage,
  onImageChange,
  disabled = false,
  className = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [originalType, setOriginalType] = useState<string | undefined>(undefined);  
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(1);
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const resetEditorState = () => {
    setIsModalOpen(false);
    setImgSrc('');
    setOriginalType(undefined);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setScale(1);
    setRotate(0);
    setAspect(1);
  };

  const onSelectFile = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setOriginalType(file.type || undefined);  
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setIsModalOpen(true);
      });
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onSelectFile,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.avif']  
    },
    multiple: false,
    disabled
  });

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initial = centerCrop(
      makeAspectCrop(
        { unit: '%', width: 90 },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(initial);
  }, []);

  const onDownloadCropClick = useCallback(() => {
    if (!previewCanvasRef.current || !completedCrop) return;

    const canvas = previewCanvasRef.current;
    // Prefer the file’s original MIME; if not available, infer from data URL
    const inferredType = originalType || getMimeFromDataUrl(imgSrc);
    // Preserve alpha if the source supports it; fall back to PNG (still alpha-safe)
    const outputType = pickOutputType(inferredType, 'image/png');

    // JPEG quality is ignored for alpha formats; leave undefined
    const quality = ALPHA_SAFE_TYPES.has(outputType) ? undefined : 0.9;

    const croppedImageData = canvas.toDataURL(outputType, quality as any);
    onImageChange(croppedImageData);
    resetEditorState();
  }, [completedCrop, onImageChange, imgSrc, originalType]);

  useEffect(() => {
    if (!completedCrop || !imgRef.current || !previewCanvasRef.current) return;

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    // Clear with full transparency to keep alpha channel
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;
    const rotateRads = rotate * (Math.PI / 180);

    ctx.save();
    ctx.translate(-cropX, -cropY);
    ctx.translate(centerX, centerY);
    ctx.rotate(rotateRads);
    ctx.scale(scale, scale);
    ctx.translate(-centerX, -centerY);
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);
    ctx.restore();
  }, [completedCrop, scale, rotate]);

  const handleSave = () => onDownloadCropClick();

  const handleCancel = () => {
    resetEditorState();
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    // also clear editor state just in case
    resetEditorState();
  };

  return (
    <div className={`image-editor ${className}`}>
      <div className="flex flex-col items-center space-y-4">
        <div
          {...(!disabled ? getRootProps() : {})}
          className={`relative ${!disabled ? 'cursor-pointer' : ''}`}
        >
          <input {...(!disabled ? getInputProps() : {})} />
          {currentImage ? (
            <img
              src={currentImage}
              alt="Profile"
              className={`w-32 h-32 rounded-full object-cover border-4 shadow-sm ${isDragActive ? 'border-blue-400' : 'border-gray-200'}`}
              style={getTransparentImageStyles()}
            />
          ) : (
            <div
              className={`w-32 h-32 rounded-full flex items-center justify-center text-gray-400 border-2 border-dashed ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-100'}`}
            >
              <span className="text-xs">Upload photo</span>
            </div>
          )}
          {currentImage && !disabled && (
            <button
              onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
              type="button"
              aria-label="Remove photo"
            >
              ×
            </button>
          )}
        </div>

        {!disabled && (
          <div className="text-center">
            <p className="text-xs text-gray-500">Drag & drop or click the circle to upload</p>
          </div>
        )}

        <Modal
          open={isModalOpen}
          onCancel={handleCancel}
          onConfirm={handleSave}
          title="Edit Profile Photo"
          isImageModal={true}
        >
          <div className="space-y-4">
            <div className="flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                minWidth={100}
                minHeight={100}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                  onLoad={onImageLoad}
                  className="max-h-96"
                />
              </ReactCrop>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scale: {Math.round(scale * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={scale}
                    onChange={(e) => setScale(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rotate: {rotate}°
                  </label>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    step="1"
                    value={rotate}
                    onChange={(e) => setRotate(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={aspect === 1}
                    onChange={(e) => setAspect(e.target.checked ? 1 : undefined)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Square crop</span>
                </label>
              </div>
            </div>

            {completedCrop && (
              <div className="flex justify-center">
                <div className="w-40 h-40 rounded-full overflow-hidden border shadow-sm bg-transparent">
                  <canvas ref={previewCanvasRef} style={{ width: 160, height: 160 }} />
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

// Utility export if you still need a Blob elsewhere:
export const generateCroppedImage = (
  image: HTMLImageElement,
  crop: PixelCrop,
  scale: number = 1,
  rotate: number = 0,
  originalSrc?: string,
  originalTypeHint?: string
): Promise<Blob | null> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return Promise.resolve(null);

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(crop.width * pixelRatio * scaleX);
  canvas.height = Math.floor(crop.height * pixelRatio * scaleY);

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.imageSmoothingQuality = 'high';
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;
  const rotateRads = rotate * (Math.PI / 180);
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);
  ctx.restore();

  const inferredType = originalTypeHint || getMimeFromDataUrl(originalSrc);
  const outputType = pickOutputType(inferredType, 'image/png');
  const quality = ALPHA_SAFE_TYPES.has(outputType) ? undefined : 0.9;

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      outputType,
      quality as any
    );
  });
};
